import * as rotulos from '../../bureaus.rotulos';
import { BUREAU_TAMANHO_PAGINA, IBureauTenantResumo } from '../../models/bureaus.model';
import {
    IBureauEquipeAdministrador,
    IBureauEquipeVinculoAdministrador,
} from '../../models/bureaus-equipes.model';
import { MeurhBureausEquipesService } from '../../bureaus-equipes.service';
import { ModalConfirmService } from '../../../../../shared/components/modal-confirm/modal.confirm.service';

export class MeurhBureausEquipesAdministradoresController {
    static $inject = [
        'MeurhBureausEquipesService',
        'ModalConfirmService',
        'NewToaster',
    ];

    /** Muda depois de uma inclusão pelo botão do título da tela. */
    public recarga: number;
    /** Avisa a tela quando a API responde 403. */
    public aoSemPermissao: () => void;
    public administradores: IBureauEquipeAdministrador[] = [];
    /** Total de pessoas, do `full_count` da primeira página. */
    public totalLista = 0;
    /** A última página veio incompleta: não há mais pessoas a carregar. */
    public fim = true;
    public carregandoMais = false;
    public filtro = '';
    /** Indexados por `bureauusuario`. */
    public novoTenantId: Record<string, number | null> = {};
    public vinculando: Record<string, boolean> = {};

    constructor(
        public meurhBureausEquipesService: MeurhBureausEquipesService,
        private modalConfirmService: ModalConfirmService,
        private NewToaster: any,
    ) { }

    public $onInit(): void {
        this.carregar();
    }

    public $onChanges(changes: angular.IOnChangesObject): void {
        if (changes.recarga && !changes.recarga.isFirstChange()) {
            this.carregar();
        }
    }

    public carregar(): void {
        this.carregandoMais = false;

        this.meurhBureausEquipesService
            .getAdministradores(this.filtro)
            .then((administradores) => {
                this.administradores = administradores || [];
                this.totalLista = this.administradores.length > 0 ? this.administradores[0].full_count || 0 : 0;
                this.fim = this.administradores.length < BUREAU_TAMANHO_PAGINA;
            })
            .catch((error) => {
                this.tratarErroCarga(error);
            });
    }

    public carregarMais(): void {
        const ultimo = this.administradores[this.administradores.length - 1];
        this.carregandoMais = true;

        this.meurhBureausEquipesService
            .getAdministradores(this.filtro, { created_at: ultimo.created_at, bureauusuario: ultimo.bureauusuario })
            .then((administradores) => {
                this.administradores = this.administradores.concat(administradores || []);
                this.fim = (administradores || []).length < BUREAU_TAMANHO_PAGINA;
            })
            .catch((error) => {
                this.tratarErroCarga(error);
            })
            .finally(() => {
                this.carregandoMais = false;
            });
    }

    public buscar(): void {
        this.carregar();
    }

    public total(): number {
        return this.totalLista;
    }

    public rotuloTenant(tenant: IBureauTenantResumo): string {
        return rotulos.rotuloTenant(tenant);
    }

    public nomePessoa(administrador: IBureauEquipeAdministrador): string {
        return rotulos.nomePessoa(administrador);
    }

    public tenantIdValido(administrador: IBureauEquipeAdministrador): boolean {
        return this.tenantIdInformado(administrador) !== null;
    }

    public vincularTenant(administrador: IBureauEquipeAdministrador): void {
        const tenantId = this.tenantIdInformado(administrador);

        if (tenantId === null) {
            this.NewToaster.pop({
                type: 'error',
                title: 'Erro ao vincular tenant',
                body: 'Informe um ID de tenant válido.'
            });
            return;
        }

        this.vinculando[administrador.bureauusuario] = true;

        this.meurhBureausEquipesService
            .createAdministradorTenant(administrador.bureauusuario, tenantId)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Tenant vinculado com sucesso!'
                });

                this.novoTenantId[administrador.bureauusuario] = null;
                this.carregar();
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao vincular tenant',
                    body: error?.data?.message || 'Ocorreu um erro ao vincular o tenant.'
                });
            })
            .finally(() => {
                this.vinculando[administrador.bureauusuario] = false;
            });
    }

    public removerTenant(administrador: IBureauEquipeAdministrador, vinculo: IBureauEquipeVinculoAdministrador): void {
        const nome = this.nomePessoa(administrador);
        const tenant = this.rotuloTenant(vinculo.tenant);
        const ultimo = administrador.tenants.length === 1;

        const modal = this.modalConfirmService.open(
            vinculo,
            'tenant',
            true,
            {
                title: 'Remover tenant do administrador',
                subtitle: ultimo
                    ? `Você está prestes a remover o tenant ${tenant}, o último de ${nome}.`
                    : `Você está prestes a remover o tenant ${tenant} de ${nome}.`,
                confirmText: ultimo
                    ? `Ao removê-lo, ${nome} deixa de ser administrador de equipe. Tem certeza que deseja remover?`
                    : 'Tem certeza que deseja remover este tenant?',
                closeButton: 'Cancelar',
                confirmButton: 'Sim, remover',
            },
            true,
        );

        modal.result
            .then(() => {
                this.executarRemocaoTenant(administrador, vinculo);
            })
            .catch(() => undefined);
    }

    public removerAdministrador(administrador: IBureauEquipeAdministrador): void {
        const nome = this.nomePessoa(administrador);

        const modal = this.modalConfirmService.open(
            administrador,
            'administrador',
            true,
            {
                title: 'Remover administrador de equipe',
                subtitle: `Você está prestes a remover ${nome} dos administradores de equipe.`,
                confirmText: `Os membros dos tenants que ${nome} administra continuam atendendo. Tem certeza que deseja remover?`,
                closeButton: 'Cancelar',
                confirmButton: 'Sim, remover',
            },
            true,
        );

        modal.result
            .then(() => {
                this.executarRemocaoAdministrador(administrador);
            })
            .catch(() => undefined);
    }

    private executarRemocaoTenant(administrador: IBureauEquipeAdministrador, vinculo: IBureauEquipeVinculoAdministrador): void {
        this.meurhBureausEquipesService
            .deleteAdministradorTenant(administrador.bureauusuario, vinculo.bureauequipeadministrador)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Tenant removido com sucesso!'
                });

                this.carregar();
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao remover tenant',
                    body: error?.data?.message || 'Ocorreu um erro ao remover o tenant.'
                });
            });
    }

    private executarRemocaoAdministrador(administrador: IBureauEquipeAdministrador): void {
        this.meurhBureausEquipesService
            .deleteAdministrador(administrador.bureauusuario)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Administrador de equipe removido com sucesso!'
                });

                this.carregar();
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao remover administrador',
                    body: error?.data?.message || 'Ocorreu um erro ao remover o administrador de equipe.'
                });
            });
    }

    /** O backend valida `tenant_id` com `is_int()`, então só um inteiro positivo pode ser enviado. */
    private tenantIdInformado(administrador: IBureauEquipeAdministrador): number | null {
        const valor: any = this.novoTenantId[administrador.bureauusuario];

        if (valor === null || valor === undefined || valor === '') {
            return null;
        }

        const tenantId = parseInt(valor, 10);

        return Number.isInteger(tenantId) && tenantId > 0 ? tenantId : null;
    }

    private tratarErroCarga(error: any): void {
        if (error?.xhrStatus === 'abort') {
            return;
        }

        if (error?.status === 403) {
            this.aoSemPermissao();
            return;
        }

        this.NewToaster.pop({
            type: 'error',
            title: 'Erro ao carregar administradores de equipe',
            body: error?.data?.message || 'Ocorreu um erro ao carregar os administradores de equipe.'
        });
    }
}
